// src/utils/authHelper.ts
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FIREBASE_COLLECTIONS } from '../Component/api/url';

const USERS_COLLECTION = FIREBASE_COLLECTIONS.USERS;

/**
 * Set the current user ID for Firebase chat.
 *
 * CRITICAL: This must be called right after login.
 * The stored value MUST match exactly what is in the Firestore members array.
 *
 * Example: nutrist_id=8, role="nutritionist" → stores "nutritionistid_8"
 * Firestore members: ["userid_11", "nutritionistid_8", "adminid_2"] ← must match exactly
 */
export const setFirebaseChatUser = (nutristId: number, userRole?: string) => {
  const role = userRole?.toLowerCase() === 'admin' ? 'admin' : 'nutritionist';
  const firebaseUserId = `${role}id_${nutristId}`;

  localStorage.setItem('firebaseChatUserId', firebaseUserId);
  localStorage.setItem('firebaseChatUserRole', role);

  console.log('✅ firebaseChatUserId stored as:', firebaseUserId);
};

/**
 * Get the Firebase chat user ID.
 * Returns the full prefixed string e.g. "nutritionistid_8".
 * Returns null if missing or incorrectly formatted.
 */
export const getFirebaseChatUserId = (): string | null => {
  const stored = localStorage.getItem('firebaseChatUserId');

  if (!stored) {
    console.warn('⚠️ firebaseChatUserId not in localStorage — call initializeFirebaseChatUser() at login');
    return null;
  }

  const valid =
    stored.startsWith('nutritionistid_') ||
    stored.startsWith('adminid_') ||
    stored.startsWith('userid_');

  if (!valid) {
    console.error(
      '❌ firebaseChatUserId is missing prefix!',
      '\n   Stored value:', stored,
      '\n   Expected format: "nutritionistid_8"',
      '\n   This will cause Firestore array-contains query to match NOTHING.',
      '\n   Fix: ensure initializeFirebaseChatUser(user) is called after login.'
    );
    return null;
  }

  return stored;
};

/**
 * Get the Firebase chat user role.
 */
export const getFirebaseChatUserRole = (): 'nutritionist' | 'admin' | null => {
  return localStorage.getItem('firebaseChatUserRole') as 'nutritionist' | 'admin' | null;
};

/**
 * Clear Firebase chat user data — call this on logout.
 */
export const clearFirebaseChatUser = () => {
  localStorage.removeItem('firebaseChatUserId');
  localStorage.removeItem('firebaseChatUserRole');
  console.log('🗑 Firebase chat user cleared from localStorage');
};

/**
 * Check if Firebase chat user ID is stored and valid.
 */
export const isFirebaseChatUserSet = (): boolean => {
  return getFirebaseChatUserId() !== null;
};

/**
 * Format a prefixed ID for display.
 * e.g. "nutritionistid_7" → "Nutritionist 7"
 */
export const formatUserId = (userId: string): string => {
  if (userId.startsWith('nutritionistid_')) return `Nutritionist ${userId.replace('nutritionistid_', '')}`;
  if (userId.startsWith('adminid_')) return `Admin ${userId.replace('adminid_', '')}`;
  if (userId.startsWith('userid_')) return `User ${userId.replace('userid_', '')}`;
  return userId;
};

/**
 * Extract the numeric portion from a prefixed ID.
 * e.g. "nutritionistid_8" → "8"
 */
export const extractNumericId = (fullId: string): string => {
  const match = fullId.match(/\d+$/);
  return match ? match[0] : fullId;
};

/**
 * Initialize Firebase chat user from the AuthContext user object.
 *
 * Called in AuthContext at:
 *   - login()
 *   - fetchAndUpdateProfile()
 *   - updateUserProfile()
 *   - useEffect on mount (restore from cache)
 *
 * @param user - User object with nutrist_id and nutrist_assign_role
 */
export const initializeFirebaseChatUser = (user: {
  nutrist_id: number;
  nutrist_assign_role?: string;
}) => {
  if (!user?.nutrist_id) {
    console.warn('⚠️ initializeFirebaseChatUser: missing nutrist_id', user);
    return;
  }
  setFirebaseChatUser(user.nutrist_id, user.nutrist_assign_role);
};

/**
 * Sync the Firestore user document with the latest profile data from the API.
 *
 * After login / profile refresh:
 *   1. Derives the prefixed Firestore doc ID (e.g. "nutritionistid_8")
 *   2. If the doc does NOT exist  → creates it with full profile data
 *   3. If the doc DOES exist      → patches only the profile fields
 *
 * NOTE on profile_url:
 *   Only written when the API returns a non-empty value, to avoid
 *   overwriting an existing photo URL with an empty string.
 *
 * Call this wherever initializeFirebaseChatUser() is called so the
 * Firestore record always reflects the latest API data.
 *
 * Called in AuthContext at:
 *   - login()
 *   - fetchAndUpdateProfile()
 *   - updateUserProfile()
 */
export const syncFirebaseUserProfile = async (user: {
  nutrist_id: number;
  nutrist_assign_role?: string;
  nutrist_fname?: string;
  nutrist_lname?: string;
  nutrist_profile?: string;
  nutrist_email?: string;
  nutrist_mobileno?: string;
  nutrist_status?: string;
}): Promise<void> => {
  if (!user?.nutrist_id) {
    console.warn('⚠️ syncFirebaseUserProfile: missing nutrist_id');
    return;
  }

  const role =
    user.nutrist_assign_role?.toLowerCase() === 'admin' ? 'admin' : 'nutritionist';
  const firebaseUserId = `${role}id_${user.nutrist_id}`;

  try {
    const userDocRef = doc(db, USERS_COLLECTION, firebaseUserId);
    const snap = await getDoc(userDocRef);

    // Fields that should always reflect the latest API data
    const profileFields: Record<string, any> = {
      user_fname:     user.nutrist_fname    || '',
      user_lname:     user.nutrist_lname    || '',
      user_email:     user.nutrist_email    || '',
      user_mobile:    user.nutrist_mobileno || '',
      nutrist_status: user.nutrist_status   || '',
      user_id:        String(user.nutrist_id),
      updatedAt:      serverTimestamp(),
    };

    // Only update profile_url when the API actually returns one
    // — prevents overwriting an existing photo with an empty string
    if (user.nutrist_profile && user.nutrist_profile.trim() !== '') {
      profileFields.profile_url = user.nutrist_profile;
    }

    if (!snap.exists()) {
      // ── Doc missing → create a full document ──────────────────────────────
      console.log('🆕 syncFirebaseUserProfile: creating doc for', firebaseUserId);
      await setDoc(userDocRef, {
        ...profileFields,
        // Set a blank profile_url on creation if none provided
        profile_url:  user.nutrist_profile || '',
        onlineStatus: 'offline',
        isActive:     false,
        lastSeen:     serverTimestamp(),
      });
      console.log('✅ Firebase user doc created:', firebaseUserId);
    } else {
      // ── Doc exists → patch profile fields only ─────────────────────────────
      console.log('🔄 syncFirebaseUserProfile: updating doc for', firebaseUserId);
      await updateDoc(userDocRef, profileFields);
      console.log('✅ Firebase user doc updated:', firebaseUserId);
    }
  } catch (error) {
    console.error('❌ syncFirebaseUserProfile error for', firebaseUserId, ':', error);
  }
};